import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Module-level Set to track codes that have been processed
// This survives React StrictMode double-execution and component re-mounts
const processedCodes = new Set<string>();
let isCurrentlyProcessing = false;

const GoogleAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshToken } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const error = searchParams.get('error');
      const code = searchParams.get('code');
      const accessToken = searchParams.get('access_token');
      const refreshTokenValue = searchParams.get('refresh_token');
      const isNewUser = searchParams.get('is_new_user') === 'true';

      // If we have a code but no tokens, it means Google redirected directly to frontend
      // We need to send the code to backend to exchange for tokens
      if (code && !accessToken && !error) {
        // Check if this code was already processed or is currently being processed
        if (processedCodes.has(code) || isCurrentlyProcessing) {
          console.log('Already processing or processed this code, waiting for completion...');
          
          // Wait for the other process to complete and check for token
          let attempts = 0;
          const maxAttempts = 20; // 10 seconds max wait
          
          const waitForToken = () => {
            attempts++;
            const token = localStorage.getItem('access_token');
            
            if (token) {
              console.log('Token found, redirecting...');
              navigate('/', { replace: true });
            } else if (attempts < maxAttempts) {
              setTimeout(waitForToken, 500);
            } else {
              // Timeout - clear and allow retry
              console.log('Timeout waiting for token, allowing retry...');
              processedCodes.delete(code);
              isCurrentlyProcessing = false;
              setStatus('error');
              setErrorMessage('Sign in timed out. Please try again.');
            }
          };
          
          setTimeout(waitForToken, 500);
          return;
        }
        
        // Mark as processing immediately (atomic)
        isCurrentlyProcessing = true;
        processedCodes.add(code);
        
        try {
          setStatus('loading');
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
          const response = await fetch(`${apiBaseUrl}/auth/oauth/google`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              code,
              redirect_uri: `${window.location.origin}/auth/google/callback`
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ success: false, error: { message: 'Failed to exchange code' } }));
            if (errorData.success === false && errorData.error) {
              throw new Error(errorData.error.message || 'Failed to exchange authorization code');
            }
            throw new Error(errorData.message || 'Failed to exchange authorization code');
          }

          const data = await response.json();
          
          if (data.success && data.data) {
            // Store tokens
            console.log('=== STORING TOKENS ===');
            console.log('accessToken:', data.data.accessToken ? 'exists' : 'missing');
            console.log('refreshToken:', data.data.refreshToken ? 'exists' : 'missing');
            
            localStorage.setItem('access_token', data.data.accessToken);
            localStorage.setItem('refresh_token', data.data.refreshToken);
            
            // Verify tokens were saved
            const savedAccess = localStorage.getItem('access_token');
            const savedRefresh = localStorage.getItem('refresh_token');
            console.log('Saved access_token:', savedAccess ? 'yes' : 'no');
            console.log('Saved refresh_token:', savedRefresh ? 'yes' : 'no');

            // Update auth state sebelum navigate
            try {
              await refreshToken();
            } catch (err) {
              console.warn('Refresh failed, will retry on next page');
            }

            // Mark as done processing
            isCurrentlyProcessing = false;
            setStatus('success');
            
            toast.success(data.data.isNewUser ? 'Account created successfully!' : 'Signed in successfully!');
            
            // Client-side navigate (tanpa full reload)
            console.log('Redirecting to / with client-side navigation...');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 1000);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error: any) {
          console.error('Error handling Google auth callback:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Failed to complete sign in');
          // Clear on error so user can retry with new code
          processedCodes.delete(code);
          isCurrentlyProcessing = false;
        }
        return;
      }

      if (error) {
        setStatus('error');
        setErrorMessage(getErrorMessage(error));
        return;
      }

      if (accessToken && refreshTokenValue) {
        try {
          // Store tokens
          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('refresh_token', refreshTokenValue);

          // Refresh auth context to get user info
          try {
            await refreshToken();
            toast.success(isNewUser ? 'Account created successfully!' : 'Signed in successfully!');
            // Redirect to home
            setTimeout(() => {
              navigate('/');
            }, 1000);
          } catch (refreshError) {
            // If refresh fails, still redirect but user will need to refresh page
            console.warn('Failed to refresh auth context:', refreshError);
            navigate('/', { replace: true });
          }
        } catch (error: any) {
          console.error('Error handling Google auth callback:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Failed to complete sign in');
        }
      } else if (!code) {
        setStatus('error');
        setErrorMessage('Missing authentication tokens or authorization code');
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshToken]);

  const getErrorMessage = (error: string): string => {
    switch (error) {
      case 'access_denied':
        return 'You cancelled the sign in process';
      case 'no_code':
        return 'Authorization code not received from Google';
      case 'no_email':
        return 'Could not retrieve email from Google account';
      default:
        return `Sign in failed: ${error}`;
    }
  };

  const handleRetry = () => {
    window.location.href = '/';
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Completing sign in...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-center">Sign In Failed</CardTitle>
            <CardDescription className="text-center">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleRetry} className="w-full">
              Return to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <p className="text-sm text-muted-foreground">Sign in successful! Redirecting...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleAuthCallback;
